/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import defaultOptions from './defaultOptions';
import { b64DecodeUnicode } from '../../utils/common';
import { has } from 'lodash';

const ConfigContext = React.createContext({});

const getApis = (config) => {
  if (config.encodedApis) {
    return config.encodedApis.map((encodedApi) => JSON.parse(b64DecodeUnicode(encodedApi)));
  } else {
    return config.apis;
  }
};

const getConfig = (options, apps, apis, encodedApis, docs) => {
  let config = typeof window !== 'undefined' && window.apiConnectExplorer || {};

  if (typeof window !== 'undefined') {
    if (has(window, 'drupalSettings.api.explorer')) {
      config = window.drupalSettings.api.explorer;
    } else if (has(window, 'Drupal.settings.api.explorer')) {
      // support for Drupal7 style Drupal.settings
      config = window.Drupal.settings.api.explorer;
    }
  }

  // Use props instead if provided
  config.options = options ? options : config.options;
  config.apps = apps ? apps : config.apps;
  config.apis = apis || encodedApis ? getApis({ apis, encodedApis }) : getApis(config);
  config.docs = docs ? docs : config.docs;
  config.constants = {
    HASH_ROUTER: 'hash',
    MEMORY_ROUTER: 'memory'
  }
  config.options = Object.assign({}, defaultOptions, config.options);

  return config
}

const ConfigProvider = ({ options, apps, apis, encodedApis, docs, children }) => {
  const [configState, updateConfig] = useState(() => getConfig(options, apps, apis, encodedApis, docs));

  const explorerUpdateListener = () => {
    updateConfig(Object.assign({}, getConfig(options, apps, apis, encodedApis, docs)));
  };

  useEffect(() => {
    updateConfig(getConfig(options, apps, apis, encodedApis, docs));
  }, [options, apps, apis, encodedApis, docs])

  useEffect(() => {
    window.addEventListener('apiConnectExplorerUpdate', explorerUpdateListener);
    return () => {
      window.removeEventListener('apiConnectExplorerUpdate', explorerUpdateListener);
    }
  }, []);

  return (
    <ConfigContext.Provider value={configState} >
      {children}
    </ConfigContext.Provider>
  );
};

const useConfig = () => useContext(ConfigContext);

ConfigProvider.propTypes = {
  options: PropTypes.object, // application options
  apps: PropTypes.array, // apps to show
  apis: PropTypes.array, // apis to show
  encodedApis: PropTypes.array, // encoded apis to show - overwrites apis
  docs: PropTypes.array, // documentation to show
}

export default ConfigProvider
export {
  useConfig,
  ConfigContext
}
